import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { alterText, betragText, zeitpunktText } from '@/lib/kopfZeit';
import type { Bestellung, ShopStatus } from '@/types/detail';

// Die Eckdaten oben in der Bestellung (Wunsch 29.10.2026): wann, wie,
// bezahlt? Bisher stand dort der Erfasser, aber kein Datum.
//
//   Alle:   Erfasst 28.10.2026, 17:47 · seit 1 Tag · Laden · Bezahlt
//   Glomer: zusätzlich eine Zeile live aus Shopify — Bestelldatum laut
//           Shop, Zahlung (+ Zahlungsart), Ausführung, Summe.
//
// BEZAHLT, ZWEI QUELLEN, NIE BEIDE: Bei Ladenbestellungen ist es der
// Checklisten-Haken (gezeigt, nicht doppelt gespeichert). Bei Glomer
// führt Shopify die Zahlung — dort gilt allein der Shop-Status; der
// Haken wird oben nicht gezeigt, sonst stünden zwei Aussagen nebeneinander,
// die auseinanderlaufen können (Prinzip 4).

const AUSSTEHEND = new Set(['PENDING', 'AUTHORIZED', 'PARTIALLY_PAID', 'EXPIRED']);

export function KopfDaten({ b }: { b: Bestellung }) {
  const ausShop = b.shopify_order_id !== null;
  const zeit = zeitpunktText(b.erfasst_zeit ?? null);
  const alter = alterText(b.erfasst_zeit ?? null);
  const bezahltHaken = b.checklist.find((c) => c.code === 'bezahlt');

  const teile: React.ReactNode[] = [];
  teile.push(<span key="erfasst">Erfasst {zeit ?? b.erfasst_am}</span>);
  if (alter) teile.push(<span key="alter">{alter}</span>);
  if (b.bestellweg?.label) teile.push(<span key="weg">{b.bestellweg.label}</span>);
  if (!ausShop && bezahltHaken) {
    teile.push(
      <span key="bezahlt" className={bezahltHaken.erledigt ? 'text-teal' : 'text-coral'}>
        {bezahltHaken.erledigt ? 'Bezahlt' : 'Noch nicht bezahlt'}
      </span>
    );
  }

  return (
    <div className="space-y-0.5 text-xs text-ink-soft" data-testid="kopf-daten">
      <p className="flex flex-wrap gap-x-2">
        {teile.map((t, i) => (
          <span key={i} className="inline-flex gap-x-2">
            {i > 0 && <span aria-hidden className="text-ink-faint">·</span>}
            {t}
          </span>
        ))}
      </p>
      {ausShop && <ShopZeile bestellnr={b.bestellnr} />}
    </div>
  );
}

function ShopZeile({ bestellnr }: { bestellnr: string }) {
  const q = useQuery({
    queryKey: ['shop-status', bestellnr],
    queryFn: () =>
      apiFetch<{ status: ShopStatus }>(
        `/bestellungen/shop-status.php?bestellnr=${encodeURIComponent(bestellnr)}`
      ),
    retry: false,
    staleTime: 60_000
  });

  if (q.isPending) return <p className="text-ink-faint">Shop: wird geladen …</p>;
  if (q.isError) return <p className="text-ink-faint">Shop: gerade nicht abrufbar</p>;

  const s = q.data.status;
  const teile: React.ReactNode[] = [];
  const bestellt = zeitpunktText(s.bestellt_am);
  if (bestellt) teile.push(<span key="b">bestellt {bestellt}</span>);
  if (s.storniert_am) {
    teile.push(
      <span key="st" className="text-coral">
        storniert {zeitpunktText(s.storniert_am)}
      </span>
    );
  }
  if (s.zahlung) {
    const arten = s.zahlungsarten.length > 0 ? ` (${s.zahlungsarten.join(', ')})` : '';
    teile.push(
      <span
        key="z"
        className={
          s.zahlung_code === 'PAID'
            ? 'text-teal'
            : AUSSTEHEND.has(s.zahlung_code ?? '')
              ? 'font-medium text-coral'
              : ''
        }
      >
        {s.zahlung}
        {arten}
      </span>
    );
  }
  if (s.ausfuehrung) teile.push(<span key="a">{s.ausfuehrung}</span>);
  const summe = betragText(s.summe, s.waehrung);
  if (summe) teile.push(<span key="s" className="tabular-nums">{summe}</span>);

  return (
    <p className="flex flex-wrap gap-x-2" data-testid="shop-status">
      <span className="text-ink-faint">Shop:</span>
      {teile.map((t, i) => (
        <span key={i} className="inline-flex gap-x-2">
          {i > 0 && <span aria-hidden className="text-ink-faint">·</span>}
          {t}
        </span>
      ))}
    </p>
  );
}
